import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '@/services/userService';
import {
  User,
  CreateUserPayload,
  UpdateUserPayload,
  QUERY_KEYS,
} from '@/types';
import { toast } from 'sonner';
import { AxiosError } from 'axios';

interface ErrorResponse {
  detail?: string;
  title?: string;
  [key: string]: unknown;
}

/**
 * Hook to fetch all users (Admin only)
 */
export const useUsers = () => {
  return useQuery({
    queryKey: QUERY_KEYS.users,
    queryFn: () => userService.getAll().then((res) => res.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a single user by id
 */
export const useUser = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.user(id),
    queryFn: () => userService.getById(id).then((res) => res.data),
    enabled: enabled && !!id,
  });
};

/**
 * Hook to check email availability (debounced in component)
 */
export const useCheckEmailAvailability = (email: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: QUERY_KEYS.userEmailAvailability(email),
    queryFn: () => userService.checkEmailAvailability(email).then((res) => res.data),
    enabled: enabled && email.length > 0,
    staleTime: 30 * 1000, // 30 seconds
    retry: false,
  });
};

/**
 * Hook to create a new user (Admin only)
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateUserPayload) => userService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      toast.success('User created successfully');
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.response?.data ||
        'Failed to create user';
      toast.error(typeof message === 'string' ? message : 'Failed to create user');
    },
  });
};

/**
 * Hook to update an existing user
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateUserPayload }) =>
      userService.update(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(variables.id) });
      toast.success('User updated successfully');
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.response?.data ||
        'Failed to update user';
      toast.error(typeof message === 'string' ? message : 'Failed to update user');
    },
  });
};

/**
 * Hook to delete a user (Admin only)
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => userService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      toast.success('User deleted successfully');
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.response?.data ||
        'Failed to delete user';
      toast.error(typeof message === 'string' ? message : 'Failed to delete user');
    },
  });
};

/**
 * Hook to get current authenticated user profile
 */
export const useCurrentUserProfile = (enabled: boolean = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.currentUser,
    queryFn: () => userService.getCurrentUser().then((res) => res.data),
    enabled: enabled, // ✅ FIXED: Remove sessionStorage check - AuthContext controls via hasToken
    retry: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};
